import {
	AssessmentOutlined as AssessmentOutlinedIcon,
	AssignmentOutlined as AssignmentOutlinedIcon,
	BeachAccess as BeachAccessIcon,
	DateRange as DateRangeIcon,
	ExitToApp as ExitToAppIcon,
	HelpOutlineOutlined,
	HomeOutlined as HomeOutlinedIcon,
	ManageAccountsOutlined as ManageAccountsOutlinedIcon,
	ResetTv as ResetTvIcon,
	SettingsOutlined as SettingsOutlinedIcon,
	SwapHoriz as SwapHorizIcon,
	Today as TodayIcon,
	WorkOutlineOutlined as WorkOutlinedIcon,
} from "@mui/icons-material";
import { useConfirm } from "material-ui-confirm";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	EVALUATION_PERMISSIONS,
	EVALUATION_SETUP_PERMISSIONS,
	FULL_TIME_SCHEDULER_PERMISSIONS,
	SETTINGS_PERMISSIONS,
} from "../../../config/permissions";
import { assertVakaySSO, resetAssertVakaySSO } from "../../../features/user/user-slice";
import { buildHelpSupportUrl } from "../../../utils/help-support";
import { HELP_TIER_SUPERVISOR } from "../../../utils/help-support";
import { hasPermission, hasPermissionString } from "../../../utils/helpers";
import { StoreType } from "../../../utils/types";
import useCrossSkillingAccess from "../../cross-skilling-dashboard/controllers/use-cross-skilling-access";
import { HOME_MENU_ITEM_ID } from "../../supervisor-home/utils/constants";
// Enable only for boeing
import powerBILogo from "../assets/dashboard.png";

type MenuItem = {
	id: number;
	name: string;
	url?: string;
	icon?: React.ReactNode;
	subMenu?: MenuItem[];
	onClick?: () => void;
}

interface UseDrawerControllerResult {
	topMenuItems: MenuItem[];
	bottomMenuItems: MenuItem[];
	defaultSelectedMenuItem: number;
}

const useDrawerController = (): UseDrawerControllerResult => {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const confirm = useConfirm();

	const logOut: MenuItem = {
		id: 7,
		name: t("drawer_log-out"),
		url: "/login",
		icon: <ExitToAppIcon />,
	};

	const shiftSwap: MenuItem = {
		id: 8,
		name: t("drawer_worker-requests"),
		icon: <SwapHorizIcon />,
		subMenu: [] as MenuItem[],
	};

	const regularRequests: MenuItem = {
		id: 81,
		name: t("drawer_time-off-requests"),
		url: "/worker-requests",
	};

	const flexRequests: MenuItem = {
		id: 82,
		name: t("drawer_flex-shift-requests"),
		url: "/worker-requests-flex",
	};

	const crossSkilling: MenuItem = {
		id: 6,
		name: t("drawer_cross-skilling"),
		url: "/cross-skilling",
		icon: <WorkOutlinedIcon />,
	};

	const adminConfiguration: MenuItem = {
		id: 5,
		name: t("drawer_admin-configuration"),
		url: "/admin-configuration",
		icon: <ManageAccountsOutlinedIcon />,
	};

	const settings: MenuItem = {
		id: 3,
		name: t("drawer_settings"),
		icon: <SettingsOutlinedIcon />,
		subMenu: [] as MenuItem[],
	};

	const vakay: MenuItem = {
		id: 24,
		name: t("drawer_vakay"),
		icon: <BeachAccessIcon />,
	};

	const home: MenuItem = {
		id: HOME_MENU_ITEM_ID,
		name: t("drawer_home"),
		url: "/",
		icon: <HomeOutlinedIcon />,
	};

	const flexScheduler: MenuItem = {
		id: 2,
		name: t("drawer_flex-schedule"),
		url: "/flex-schedule",
		icon: <DateRangeIcon />,
	};

	const fullTimeScheduler: MenuItem = {
		id: 1,
		name: t("drawer_advanced-schedule"),
		url: "/full-time-schedule",
		icon: <TodayIcon />,
	};

	// Enable only for boeing
	const powerBI: MenuItem = {
		id: 9,
		name: t("drawer_dashboard"),
		url: "https://app.powerbi.com/groups/me/apps/29e3957d-de20-4d27-a712-eae041e0b6cf/reports/ba7a13f6-f26a-4052-9f79-880c789b48c6/ReportSection47e6b1cfff6775079a30?ctid=b73ab130-8864-4872-85ed-e9e022d489fd&experience=power-bi",
		icon: <img src={powerBILogo} alt='' height={24} />,
	};

	const workCenters: MenuItem = { id: 11, name: t("drawer_work-centers"), url: "/workcenters" };
	const skills: MenuItem = { id: 10, name: t("drawer_skills"), url: "/manage-skills" };
	const jobRoles: MenuItem = { id: 13, name: t("drawer_job-roles"), url: "/manage-jobs" };
	const workers: MenuItem = { id: 12, name: t("drawer_workers"), url: "/manage-workers" };
	const users: MenuItem = { id: 66, name: t("drawer_users"), url: "/manage-users" };

	const skillEvaluation: MenuItem = {
		id: 23,
		name: t("drawer_evaluation-setup"),
		url: "/evaluation-setup",
	};

	const evaluations: MenuItem = {
		id: 25,
		name: t("drawer_evaluations"),
		url: "/evaluations",
		icon: <AssignmentOutlinedIcon />,
	};

	const certifications: MenuItem = {
		id: 16,
		name: t("drawer_certifications"),
		url: "/certifications",
	};

	const privacyPolicy: MenuItem = {
		id: 88,
		name: t("drawer_privacy-policy"),
		url: "https://wilya.com/privacy",
	};

	const demoSetup: MenuItem = {
		id: 15,
		name: t("drawer_demo-reset"),
		url: "/demo",
		icon: <ResetTvIcon />,
	};

	const reportsMenu: MenuItem = {
		id: 19,
		name: t("drawer_reports"),
		icon: <AssessmentOutlinedIcon />,
		subMenu: [] as MenuItem[],
	};

	const attendanceReports: MenuItem = {
		id: 17,
		name: t("drawer_attendance-report"),
		url: "/reports",
	};

	const skillInsights: MenuItem = {
		id: 18,
		name: t("drawer_skill-summary-report"),
		url: "/insight",
	};

	const absenceReports: MenuItem = {
		id: 20,
		name: t("drawer_absence-report"),
		url: "/absence-reports",
	};

	const workerActivityReports: MenuItem = {
		id: 21,
		name: t("drawer_worker-activity-report"),
		url: "/worker-activity",
	};

	const timeOffBalancesReport: MenuItem = {
		id: 22,
		name: t("drawer_leave-balance-report"),
		url: "/time-off-balance-report",
	};

	const minHours: MenuItem = {
		id: 26,
		name: t("drawer_min-hours"),
		url: "/min-hours",
	};

	// Enabled Updating leave balance
	const timeOffBalance: MenuItem = {
		id: 22,
		name: t("drawer_leave-balance"),
		url: "/time-off-balance",
	};

	const showCrossSkilling = useSelector(
		(state: StoreType) => state.user.userData.showCrossSkilling
	);
	const crossSkillingAccess = useCrossSkillingAccess();
	const certificationsEnabled = useSelector(
		(state: StoreType) => state.user.userData.certificationsEnabled
	);
	const skillExpiryEnabled = useSelector(
		(state: StoreType) => state.user.userData.skillExpiryEnabled
	);
	const defaultUserView = useSelector(
		(state: StoreType) => state.user.userData.defaultUserView
	);
	const showShiftSwap = useSelector(
		(state: StoreType) => state.user.userData.showShiftSwap
	);
	const fullTimeSchedule = useSelector(
		(state: StoreType) => state.user.userData.fullTimeSchedule
	);
	const ftSchedulerEnabled = useSelector(
		(state: StoreType) => state.user.userData.ftSchedulerEnabled
	);
	const ftSchedulerEnabledLocations = useSelector(
		(state: StoreType) => state.user.userData.ftSchedulerEnabledLocations || []
	);
	const supervisorDashboardEnabled = useSelector(
		(state: StoreType) => state.user.userData.supervisorDashboardEnabled
	);

	const roles = useSelector(
		(state: StoreType) => state.user?.userData?.userRoles
	);

	const userRolesWithPermissions = useSelector(
		(state: StoreType) => state.user?.userData?.userRolesWithPermissions
	)
	const permissions = useSelector(
		(state: StoreType) => state.user?.userData?.permissions
	);

	const userInfo = useSelector((state: StoreType) => state.user?.userData);

	const isSkillInsightsEnabled = useSelector(
		(state: StoreType) => state.user.userData.skillInsightsEnabled
	);

	const isAttendanceManagementEnabled = useSelector(
		(state: StoreType) => state.user.userData.attendanceManagement
	);

	const attendanceManagementEnabledLocations = useSelector(
		(state: StoreType) =>
			state.user.userData.attendanceManagementEnabledLocations
	);

	const enableSkillEvaluationForm = useSelector(
		(state: StoreType) => state.user.userData.EnableSkillEvaluationForm
	);

	const leaveBalanceUpdateEnabled = useSelector(
		(state: StoreType) => state.user.userData.leaveBalanceUpdateEnabled
	);

	const vakaySSOLinkEnabled = useSelector(
		(state: StoreType) => state.user.userData.vakaySSOLinkEnabled
	);

	const punchImportEnabled = useSelector(
		(state: StoreType) => state.user.userData.punchImportEnabled
	);

	const minWeekHoursNotifEnabledLocations = useSelector(
		(state: StoreType) => Array.isArray(state.user.userData.minWeekHoursNotifEnabledLocations) ? state.user.userData.minWeekHoursNotifEnabledLocations : []
	);

	const assertVakaySSOState = useSelector(
		(state: StoreType) => state.user.assertVakaySSO
	);

	useEffect(() => {
		if (assertVakaySSOState.status === "fulfilled" && assertVakaySSOState.redirectUrl) {
			const url = new URL(assertVakaySSOState.redirectUrl);
			const originalParams = new URLSearchParams(url.search);
			url.search = "";
			url.searchParams.set("from", "supervisor");
			const env = process.env.REACT_APP_ENV ?? "prod";
			url.searchParams.set("env", env === "prod" ? "app" : env);
			originalParams.forEach((value, key) => {
				url.searchParams.append(key, value);
			});
			window.open(url.toString(), "_blank");
			dispatch(resetAssertVakaySSO());
		}
	}, [assertVakaySSOState.status, assertVakaySSOState.redirectUrl, dispatch]);

	const handleVakayClick = () => {
		confirm({
			title: t("vakay-confirmation_title"),
			description: t("vakay-confirmation_description"),
			confirmationText: t("vakay-confirmation_proceed"),
			cancellationText: t("vakay-confirmation_cancel"),
		})
			.then(() => {
				dispatch(assertVakaySSO());
			})
			.catch(() => {
				// Do nothing on cancel
			});
	};

	// Configuring menu items
	const topMenuItems: MenuItem[] = [];
	const bottomMenuItems: MenuItem[] = [];
	let defaultSelectedMenuItem = 2;

	// if (fullTimeSchedule) {
	// 	topMenuItems.push(fullTimeScheduler);
	// 	defaultSelectedMenuItem = 1;
	// }

	// Home owns "/" only while the company flag is on; without it the flex
	// scheduler stays the landing page and a Home entry would be a dead link.
	if (supervisorDashboardEnabled) {
		// The Home page selects itself on mount, so defaultSelectedMenuItem is
		// left alone — it still drives the flex/FT/cross-skilling default view.
		topMenuItems.push(home);
	}

	topMenuItems.push(flexScheduler);
	if (
		ftSchedulerEnabled &&
		ftSchedulerEnabledLocations?.length > 0 &&
		hasPermissionString(permissions, FULL_TIME_SCHEDULER_PERMISSIONS.VIEW)
	) {
		defaultSelectedMenuItem = 1;
		topMenuItems.push(fullTimeScheduler);
	}

	// GAT-7289 - Jobs Matrix lives under the Cross Skilling menu item, so the
	// entry must also show when only Jobs Matrix is visible. GAT-7254 - the
	// entry follows tab visibility, not just the company flags.
	if (crossSkillingAccess.canViewPage) {
		// PART OF HOTFIX - DEMO - GAT-3286
		// if (fullTimeSchedule) {
		// 	crossSkilling.name = "Certifications";
		// }
		if (
			!crossSkillingAccess.canViewSkillsMatrix &&
			!crossSkillingAccess.canViewSummary
		) {
			crossSkilling.name = t("drawer_jobs-matrix");
		}
		if (
			crossSkillingAccess.canViewSkillsMatrix &&
			defaultUserView === "CrossSkilling"
		) {
			// If default view is cross-skilling and show cross-skilling is enabled
			defaultSelectedMenuItem = 6;
		}

		topMenuItems.push(crossSkilling);
	}

	// Worker Requests sub-menu
	shiftSwap.subMenu = [];
	if (isAttendanceManagementEnabled && attendanceManagementEnabledLocations?.length > 0) {
		shiftSwap.subMenu.push(regularRequests);
	}
	if (showShiftSwap) {
		shiftSwap.subMenu.push(flexRequests);
	}
	if (shiftSwap.subMenu.length > 0) {
		topMenuItems.push(shiftSwap);
	}

	// Enable only for Boeing
	if (fullTimeSchedule) {
		topMenuItems.push(powerBI);
	}

	//Add Reports just before the settings menu
	if (showCrossSkilling && isSkillInsightsEnabled) {
		//Add Attendance reports and skill insights
		// GAT-7253 - the branch stays on the flags so the other reports keep
		// their rules; only the Skill Summary entry needs the permission.
		reportsMenu.subMenu = crossSkillingAccess.canViewSummary
			? [attendanceReports, skillInsights]
			: [attendanceReports];
		if (isAttendanceManagementEnabled) {
			reportsMenu.subMenu.push(absenceReports);
      if (attendanceManagementEnabledLocations?.length > 0 && !(leaveBalanceUpdateEnabled && userRolesWithPermissions && (
        hasPermission(userRolesWithPermissions, "REPORTS.UPDATE_LEAVE_BALANCE")
      ))) {
        reportsMenu.subMenu.push(timeOffBalancesReport);
      }
		}
		topMenuItems.push(reportsMenu);
	} else {
		//Add only attendance reports
		if (!isAttendanceManagementEnabled) {
			reportsMenu.subMenu = [attendanceReports];
			topMenuItems.push(reportsMenu);
		} else if (attendanceManagementEnabledLocations?.length > 0) {
			if (leaveBalanceUpdateEnabled && userRolesWithPermissions && (
				hasPermission(userRolesWithPermissions, "REPORTS.UPDATE_LEAVE_BALANCE")
			)) {
				reportsMenu.subMenu = [attendanceReports, absenceReports];
			} else {
				reportsMenu.subMenu = [attendanceReports, absenceReports, timeOffBalancesReport];
			}
			topMenuItems.push(reportsMenu);
		} else {
			reportsMenu.subMenu = [attendanceReports];
			topMenuItems.push(reportsMenu);
		}
	}
	reportsMenu.subMenu.push(workerActivityReports);
	if (userRolesWithPermissions && hasPermission(userRolesWithPermissions, "REPORTS.VIEW_MIN_WEEKLY_HOURS_HISTORY") && minWeekHoursNotifEnabledLocations.length > 0) {
		reportsMenu.subMenu.push(minHours);
	}

	if (
		enableSkillEvaluationForm &&
		hasPermissionString(permissions, EVALUATION_PERMISSIONS.VIEW)
	) {
		topMenuItems.push(evaluations);
	}

	// Settings — every area carries its own view permission, so the submenu is
	// built up from what the user actually holds rather than from role names.
	settings.subMenu = [];
	if (hasPermissionString(permissions, SETTINGS_PERMISSIONS.WORKERS.VIEW)) {
		settings.subMenu.push(workers);
	}
	if (hasPermissionString(permissions, SETTINGS_PERMISSIONS.JOB_ROLES.VIEW)) {
		settings.subMenu.push(jobRoles);
	}
	if (
		showCrossSkilling &&
		hasPermissionString(permissions, SETTINGS_PERMISSIONS.SKILLS.VIEW)
	) {
		settings.subMenu.push(skills);
	}
	if (
		enableSkillEvaluationForm &&
		hasPermissionString(permissions, EVALUATION_SETUP_PERMISSIONS.VIEW)
	) {
		settings.subMenu.push(skillEvaluation);
	}

	if (
		certificationsEnabled &&
		hasPermissionString(permissions, SETTINGS_PERMISSIONS.CERTIFICATIONS.VIEW)
	) {
		settings.subMenu.push(certifications);
	}
	if (hasPermissionString(permissions, SETTINGS_PERMISSIONS.WORKCENTERS.VIEW)) {
		settings.subMenu.push(workCenters);
	}

	// Was gated on roles.includes("HR Manager") minus the admin roles, which is
	// exactly what settings.view_users is seeded to today.
	if (hasPermissionString(permissions, SETTINGS_PERMISSIONS.USERS.VIEW)) {
		settings.subMenu.push(users);
	}

	if (isAttendanceManagementEnabled && attendanceManagementEnabledLocations?.length > 0 && leaveBalanceUpdateEnabled && userRolesWithPermissions && (
		hasPermission(userRolesWithPermissions, "REPORTS.UPDATE_LEAVE_BALANCE"))) {
		settings.subMenu.push(timeOffBalance);
	}

	// Every Settings area is permission-gated, so a user can end up with none of
	// them. Mirror the Worker Requests guard rather than render a menu that
	// opens onto an empty list.
	if (
		hasPermissionString(permissions, SETTINGS_PERMISSIONS.VIEW) &&
		settings.subMenu.length > 0
	) {
		topMenuItems.push(settings);
	}

	if (roles.includes("GATAdmin") || roles.includes("Admin")) {
		topMenuItems.push(adminConfiguration);
	}

	if (roles.includes("DemoResetRole")) {
		topMenuItems.push(demoSetup);
	}

	if (vakaySSOLinkEnabled) {
		topMenuItems.push({
			...vakay,
			onClick: handleVakayClick,
		});
	}

	const appName = `${userInfo?.firstName} ${userInfo?.lastName}`.trim();
	const appContact = userInfo?.username || "";
	const appOrg = userInfo?.company || "";

	const helpUrl = buildHelpSupportUrl({
		name: appName,
		contact: appContact,
		org: appOrg,
		tier: HELP_TIER_SUPERVISOR,
		language: i18n.language,
	});

	const helpMenu: MenuItem = {
		id: 77,
		name: t("drawer_help"),
		icon: <HelpOutlineOutlined />,
		subMenu: [
			{
				id: 14,
				name: t("drawer_contact-support"),
				url: helpUrl,
			},
			privacyPolicy,
		],
	};

	bottomMenuItems.push(helpMenu);
	bottomMenuItems.push(logOut);

	return { topMenuItems, bottomMenuItems, defaultSelectedMenuItem };
};

export default useDrawerController;
